import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Camera,
  ChevronDown,
  ChevronRight,
  Filter,
  ImageOff,
  MapPin,
  Network,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";

import { ApiError, apiUploadFile, formatDate, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  type BadgeTone,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { MapaArranqueModal } from "./MapaArranqueModal";
import {
  actualizarArranque,
  asociarFotoReferencia,
  crearArranque,
  eliminarArranque,
  eliminarFotoReferencia,
  historialArranque,
  instalarMedidor,
  listarArranques,
  listarMedidores,
  retirarMedidor,
  type ArranqueCreatePayload,
  type ArranqueRead,
  type ArranqueUpdatePayload,
  type EstadoArranque,
  type GeorefOrigen,
  type MedidorRead,
} from "./api";
import { listarSectores, type SectorRead } from "../sectores/api";

const ESTADOS: EstadoArranque[] = ["Activo", "Retirado", "Inactivo"];

export function ArranquesListPage(): JSX.Element {
  const [sectorFiltro, setSectorFiltro] = useState<string>("");
  const [editing, setEditing] = useState<ArranqueRead | "new" | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const sectoresQ = useQuery({
    queryKey: ["sectores"],
    queryFn: listarSectores,
  });
  const arranquesQ = useQuery({
    queryKey: ["arranques", sectorFiltro || "todos"],
    queryFn: () =>
      listarArranques(sectorFiltro ? Number(sectorFiltro) : undefined),
  });
  const medidoresQ = useQuery({
    queryKey: ["medidores", "todos"],
    queryFn: () => listarMedidores(),
  });

  const sectorById = useMemo(() => {
    const m = new Map<number, SectorRead>();
    for (const s of sectoresQ.data ?? []) m.set(s.id, s);
    return m;
  }, [sectoresQ.data]);

  const medidorPorArranque = useMemo(() => {
    const m = new Map<number, MedidorRead>();
    for (const me of medidoresQ.data ?? []) {
      if (me.arranque_id != null) m.set(me.arranque_id, me);
    }
    return m;
  }, [medidoresQ.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Arranques"
        icon={Network}
        subtitle={
          <>
            Un arranque es un punto de suministro físico (dirección + opcional
            medidor). El código <code>ARR-NNNN</code> se autogenera. Para
            instalar/retirar un medidor expande la fila.
            {arranquesQ.data && <> ({arranquesQ.data.length} arranques)</>}
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nuevo arranque
          </Button>
        }
      />

      {editing && (
        <ArranqueForm
          arranque={editing === "new" ? null : editing}
          sectores={sectoresQ.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}

      <Card padding="sm">
        <div className="flex items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <select
            value={sectorFiltro}
            onChange={(e) => setSectorFiltro(e.target.value)}
            aria-label="Filtrar por sector"
            className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
          >
            <option value="">Todos los sectores</option>
            {(sectoresQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.codigo ? `[${s.codigo}] ` : ""}
                {s.nombre}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {arranquesQ.isPending && <Cargando />}
      {arranquesQ.isError && (
        <ErrorCarga
          error={arranquesQ.error}
          mensaje="Error al cargar arranques."
          onReintentar={() => void arranquesQ.refetch()}
        />
      )}
      {arranquesQ.data && arranquesQ.data.length === 0 && (
        <p className="text-sm text-neutral-500">Sin arranques.</p>
      )}

      {arranquesQ.data && arranquesQ.data.length > 0 && (
        <TableWrap label="Arranques">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">{""}</th>
                <th scope="col" className="px-3 py-2 text-left">Código</th>
                <th scope="col" className="px-3 py-2 text-left">Dirección</th>
                <th scope="col" className="px-3 py-2 text-left">Sector</th>
                <th scope="col" className="px-3 py-2 text-left">Medidor</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {arranquesQ.data.map((a) => (
                <ArranqueRow
                  key={a.id}
                  arranque={a}
                  sector={a.sector_id ? sectorById.get(a.sector_id) : undefined}
                  medidor={medidorPorArranque.get(a.id)}
                  expanded={expanded === a.id}
                  onToggle={() =>
                    setExpanded((p) => (p === a.id ? null : a.id))
                  }
                  onEdit={() => setEditing(a)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Fila + panel expandido
// ────────────────────────────────────────────────────────────────────────

function ArranqueRow({
  arranque,
  sector,
  medidor,
  expanded,
  onToggle,
  onEdit,
}: {
  arranque: ArranqueRead;
  sector: SectorRead | undefined;
  medidor: MedidorRead | undefined;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const eliminar = useMutation({
    mutationFn: () => eliminarArranque(arranque.id),
    onSuccess: () => {
      toast.success(`Arranque ${arranque.codigo_arranque} eliminado`);
      void qc.invalidateQueries({ queryKey: ["arranques"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo eliminar");
    },
  });

  return (
    <>
      <tr className="hover:bg-neutral-50">
        <td className="px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={expanded ? ChevronDown : ChevronRight}
            onClick={onToggle}
            aria-label={expanded ? "Cerrar panel" : "Abrir panel"}
          />
        </td>
        <td className="px-3 py-2 font-mono text-xs">
          {arranque.codigo_arranque ?? "—"}
        </td>
        <td className="px-3 py-2">
          <div className="font-medium text-neutral-900">{arranque.direccion}</div>
          {(arranque.numero_casa || arranque.referencia) && (
            <div className="text-xs text-neutral-500">
              {arranque.numero_casa && <>N° {arranque.numero_casa}</>}
              {arranque.numero_casa && arranque.referencia && " · "}
              {arranque.referencia}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-neutral-600">
          {sector ? (
            <>
              {sector.codigo && (
                <span className="font-mono text-xs">[{sector.codigo}] </span>
              )}
              {sector.nombre}
            </>
          ) : (
            "—"
          )}
        </td>
        <td className="px-3 py-2">
          {medidor ? (
            <div className="text-neutral-900">
              <div className="font-mono text-xs">{medidor.numero_serie}</div>
              <div className="text-xs text-neutral-500">
                {medidor.marca ?? "—"} · {medidor.diametro}
              </div>
            </div>
          ) : (
            <span className="text-xs text-neutral-500 italic">sin medidor</span>
          )}
        </td>
        <td className="px-3 py-2">
          <EstadoBadge estado={arranque.estado} />
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex items-center justify-end gap-3">
            <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
              Editar
            </Button>
            <Button
              variant="linkDanger"
              size="sm"
              icon={Trash2}
              onClick={() => setConfirmOpen(true)}
              disabled={eliminar.isPending}
            >
              Eliminar
            </Button>
          </div>
          <ConfirmDialog
            open={confirmOpen}
            tone="danger"
            title="Eliminar arranque"
            description={`Eliminar arranque ${arranque.codigo_arranque}? Falla si tiene contratos o medidor.`}
            confirmLabel="Eliminar"
            loading={eliminar.isPending}
            onConfirm={() => {
              setConfirmOpen(false);
              eliminar.mutate();
            }}
            onCancel={() => setConfirmOpen(false)}
          />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-neutral-50">
          <td colSpan={7} className="px-3 py-3 space-y-4">
            <UbicacionPanel arranque={arranque} />
            <FotoReferenciaPanel arranque={arranque} />
            <InstalacionPanel arranque={arranque} medidor={medidor} />
          </td>
        </tr>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Panel de ubicación (mapa Mapbox)
// ────────────────────────────────────────────────────────────────────────

function UbicacionPanel({ arranque }: { arranque: ArranqueRead }): JSX.Element {
  const [open, setOpen] = useState(false);
  const tieneCoords = arranque.latitud != null && arranque.longitud != null;
  const origenLabel = georefOrigenLabel(arranque.georef_origen);

  return (
    <div className="rounded-md border border-neutral-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <MapPin
              className={
                tieneCoords
                  ? "h-4 w-4 text-emerald-700"
                  : "h-4 w-4 text-amber-700"
              }
              strokeWidth={1.5}
            />
            <h4 className="text-sm font-semibold text-neutral-700">
              Ubicación geográfica
            </h4>
          </div>
          {tieneCoords ? (
            <div className="mt-1 text-xs text-neutral-600 space-y-0.5">
              <div className="font-mono tabular-nums">
                Lat <strong>{arranque.latitud!.toFixed(6)}</strong> · Lng{" "}
                <strong>{arranque.longitud!.toFixed(6)}</strong>
              </div>
              <div>
                {origenLabel}
                {arranque.georef_actualizado_at && (
                  <span className="text-neutral-500">
                    {" "}
                    · {formatDateTime(arranque.georef_actualizado_at)}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-1 text-xs text-amber-700">
              Este arranque todavía no tiene coordenadas. Márcalo en el mapa
              para que el operador pueda navegar hacia él.
            </p>
          )}
        </div>
        <Button size="sm" icon={MapPin} onClick={() => setOpen(true)}>
          {tieneCoords ? "Editar en mapa" : "Marcar en mapa"}
        </Button>
      </div>
      {open && (
        <MapaArranqueModal
          open={open}
          onClose={() => setOpen(false)}
          arranque={arranque}
        />
      )}
    </div>
  );
}

function georefOrigenLabel(origen: GeorefOrigen | null): string {
  switch (origen) {
    case "manual_admin":
      return "Cargada manualmente desde el admin";
    case "gps_lectura":
      return "Cargada por el GPS del operador en terreno";
    case "corregido":
      return "Corregida por el admin";
    default:
      return "Origen desconocido";
  }
}

// ────────────────────────────────────────────────────────────────────────
// Panel de instalación / retiro / historial
// ────────────────────────────────────────────────────────────────────────

function InstalacionPanel({
  arranque,
  medidor,
}: {
  arranque: ArranqueRead;
  medidor: MedidorRead | undefined;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);

  const histQ = useQuery({
    queryKey: ["arranque-historial", arranque.id],
    queryFn: () => historialArranque(arranque.id),
  });
  const bodegaQ = useQuery({
    queryKey: ["medidores", "bodega"],
    queryFn: () => listarMedidores(true),
  });

  const [medidorId, setMedidorId] = useState("");
  const [fechaIn, setFechaIn] = useState(today);
  const [lecturaIn, setLecturaIn] = useState("0");
  const [obs, setObs] = useState("");
  const [fechaOut, setFechaOut] = useState(today);
  const [lecturaOut, setLecturaOut] = useState("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const instalar = useMutation({
    mutationFn: () =>
      instalarMedidor({
        arranque_id: arranque.id,
        medidor_id: Number(medidorId),
        fecha_instalacion: fechaIn,
        lectura_al_instalar: Number(lecturaIn),
        observaciones: obs.trim() || null,
      }),
    onSuccess: () => {
      toast.success("Medidor instalado");
      setMedidorId("");
      setObs("");
      setLecturaIn("0");
      void qc.invalidateQueries({ queryKey: ["arranque-historial", arranque.id] });
      void qc.invalidateQueries({ queryKey: ["medidores"] });
      void qc.invalidateQueries({ queryKey: ["arranques"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo instalar";
      setError(msg);
      toast.error(msg);
    },
  });

  const retirar = useMutation({
    mutationFn: () =>
      retirarMedidor(arranque.id, {
        fecha_retiro: fechaOut,
        lectura_al_retirar: Number(lecturaOut),
        motivo_cambio: motivo.trim() || null,
      }),
    onSuccess: (inst) => {
      toast.success("Medidor retirado");
      if (inst.advertencia) toast.info(inst.advertencia);
      setLecturaOut("");
      setMotivo("");
      void qc.invalidateQueries({ queryKey: ["arranque-historial", arranque.id] });
      void qc.invalidateQueries({ queryKey: ["medidores"] });
      void qc.invalidateQueries({ queryKey: ["arranques"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo retirar";
      setError(msg);
      toast.error(msg);
    },
  });

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-neutral-700">
        Gestión de medidor en {arranque.codigo_arranque}
      </h4>

      {medidor ? (
        <div className="rounded-md border border-neutral-200 bg-white p-3 space-y-2">
          <div className="text-sm">
            Medidor instalado:{" "}
            <strong className="font-mono">{medidor.numero_serie}</strong>
            {medidor.marca && (
              <span className="text-neutral-500"> · {medidor.marca}</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <Input
              label="Fecha de retiro"
              type="date"
              value={fechaOut}
              onChange={(e) => setFechaOut(e.target.value)}
              disabled={retirar.isPending}
            />
            <Input
              label="Lectura al retirar"
              type="number"
              min={0}
              value={lecturaOut}
              onChange={(e) => setLecturaOut(e.target.value)}
              disabled={retirar.isPending}
            />
            <Input
              label="Motivo"
              type="text"
              maxLength={100}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="ej. reemplazo por antigüedad"
              disabled={retirar.isPending}
            />
            <Button
              variant="destructive"
              onClick={() => {
                setError(null);
                if (!fechaOut) return setError("Fecha requerida");
                if (
                  lecturaOut.trim() === "" ||
                  !Number.isFinite(Number(lecturaOut))
                )
                  return setError("Lectura al retirar requerida");
                retirar.mutate();
              }}
              disabled={retirar.isPending}
            >
              {retirar.isPending ? "Retirando…" : "Retirar medidor"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-neutral-200 bg-white p-3 space-y-2">
          <div className="text-sm text-neutral-700">
            No hay medidor instalado. Elige uno de la bodega:
          </div>
          {bodegaQ.data && bodegaQ.data.length === 0 ? (
            <p className="text-sm text-amber-700">
              Sin medidores en bodega. Carga uno desde el módulo Medidores.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
              <Select
                label="Medidor (bodega)"
                value={medidorId}
                onChange={(e) => setMedidorId(e.target.value)}
                disabled={instalar.isPending}
              >
                <option value="">— elegir —</option>
                {(bodegaQ.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.numero_serie} {m.marca ? `· ${m.marca}` : ""}
                  </option>
                ))}
              </Select>
              <Input
                label="Fecha instalación"
                type="date"
                value={fechaIn}
                onChange={(e) => setFechaIn(e.target.value)}
                disabled={instalar.isPending}
              />
              <Input
                label="Lectura inicial"
                type="number"
                min={0}
                value={lecturaIn}
                onChange={(e) => setLecturaIn(e.target.value)}
                disabled={instalar.isPending}
              />
              {/* Instalar: acción positiva emerald sin variante equivalente → nativo con la paleta del ecosistema */}
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  if (!medidorId) return setError("Elige un medidor");
                  if (!fechaIn) return setError("Fecha requerida");
                  instalar.mutate();
                }}
                disabled={instalar.isPending}
                className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {instalar.isPending ? "Instalando…" : "Instalar medidor"}
              </button>
            </div>
          )}
          <Input
            label="Observaciones (opcional)"
            type="text"
            maxLength={500}
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            disabled={instalar.isPending}
          />
        </div>
      )}

      {error && <p className="text-sm text-rose-700">{error}</p>}

      <div className="rounded-md border border-neutral-200 bg-white p-3">
        <h5 className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
          Historial de instalaciones
        </h5>
        {histQ.isPending ? (
          <Cargando inline />
        ) : (histQ.data ?? []).length === 0 ? (
          <p className="text-xs text-neutral-500">Sin historial.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-neutral-500">
              <tr>
                <th scope="col" className="text-left py-1">Medidor</th>
                <th scope="col" className="text-left py-1">Instalado</th>
                <th scope="col" className="text-right py-1">Lectura inicial</th>
                <th scope="col" className="text-left py-1">Retirado</th>
                <th scope="col" className="text-right py-1">Lectura final</th>
                <th scope="col" className="text-left py-1">Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {(histQ.data ?? []).map((i) => (
                <tr key={i.id}>
                  <td className="py-1 font-mono">#{i.medidor_id}</td>
                  <td className="py-1">{formatDate(i.fecha_instalacion)}</td>
                  <td className="py-1 text-right">{i.lectura_al_instalar}</td>
                  <td className="py-1">
                    {i.fecha_retiro ? (
                      formatDate(i.fecha_retiro)
                    ) : (
                      <span className="text-emerald-700 font-medium">
                        Activa
                      </span>
                    )}
                  </td>
                  <td className="py-1 text-right">
                    {i.lectura_al_retirar ?? "—"}
                  </td>
                  <td className="py-1 text-neutral-500">
                    {i.motivo_cambio ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form alta/edición de Arranque
// ────────────────────────────────────────────────────────────────────────

function ArranqueForm({
  arranque,
  sectores,
  onClose,
}: {
  arranque: ArranqueRead | null;
  sectores: SectorRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = arranque !== null;

  const [direccion, setDireccion] = useState(arranque?.direccion ?? "");
  const [numeroCasa, setNumeroCasa] = useState(arranque?.numero_casa ?? "");
  const [referencia, setReferencia] = useState(arranque?.referencia ?? "");
  const [sectorId, setSectorId] = useState(
    arranque?.sector_id ? String(arranque.sector_id) : "",
  );
  const [estado, setEstado] = useState<EstadoArranque>(
    arranque?.estado ?? "Activo",
  );
  const [codigo, setCodigo] = useState(arranque?.codigo_arranque ?? "");
  const [orden, setOrden] = useState(
    arranque?.orden_ruta != null ? String(arranque.orden_ruta) : "",
  );
  const [latitud, setLatitud] = useState(
    arranque?.latitud != null ? String(arranque.latitud) : "",
  );
  const [longitud, setLongitud] = useState(
    arranque?.longitud != null ? String(arranque.longitud) : "",
  );
  const [error, setError] = useState<string | null>(null);

  const crearMut = useMutation({
    mutationFn: (p: ArranqueCreatePayload) => crearArranque(p),
    onSuccess: (a) => {
      toast.success(`Arranque ${a.codigo_arranque} creado`);
      void qc.invalidateQueries({ queryKey: ["arranques"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: ArranqueUpdatePayload) =>
      actualizarArranque((arranque as ArranqueRead).id, p),
    onSuccess: (a) => {
      toast.success(`Arranque ${a.codigo_arranque} actualizado`);
      void qc.invalidateQueries({ queryKey: ["arranques"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!direccion.trim()) return setError("Dirección requerida");

    const payload = {
      direccion: direccion.trim(),
      sector_id: sectorId ? Number(sectorId) : null,
      numero_casa: numeroCasa.trim() || null,
      referencia: referencia.trim() || null,
      estado,
      codigo_arranque: codigo.trim() || null,
      orden_ruta: orden.trim() === "" ? null : Number(orden),
      latitud: latitud.trim() === "" ? null : Number(latitud),
      longitud: longitud.trim() === "" ? null : Number(longitud),
    };

    if (editando) actualizarMut.mutate(payload);
    else crearMut.mutate(payload);
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    direccion, numeroCasa, referencia, sectorId, estado, codigo, orden, latitud,
    longitud,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar arranque" : "Nuevo arranque"}
      subtitle={editando ? arranque.codigo_arranque ?? undefined : undefined}
      icon={Network}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear arranque"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="Dirección"
          required
          type="text"
          maxLength={200}
          value={direccion}
          onChange={(e) => setDireccion(e.target.value)}
          disabled={isPending}
          autoFocus
        />
        <Input
          label="N° de casa"
          type="text"
          maxLength={50}
          value={numeroCasa}
          onChange={(e) => setNumeroCasa(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Referencia"
          type="text"
          maxLength={255}
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
          disabled={isPending}
        />

        <Select
          label="Sector"
          value={sectorId}
          onChange={(e) => setSectorId(e.target.value)}
          disabled={isPending}
        >
          <option value="">— sin sector —</option>
          {sectores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.codigo ? `[${s.codigo}] ` : ""}
              {s.nombre}
            </option>
          ))}
        </Select>
        <Select
          label="Estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoArranque)}
          disabled={isPending}
        >
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </Select>
        <Input
          label={
            editando
              ? "Código (editable)"
              : "Código (vacío = autogenerado ARR-NNNN)"
          }
          type="text"
          maxLength={20}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          disabled={isPending}
        />

        <Input
          label="Orden ruta"
          type="number"
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Latitud"
          type="number"
          step="0.00001"
          value={latitud}
          onChange={(e) => setLatitud(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Longitud"
          type="number"
          step="0.00001"
          value={longitud}
          onChange={(e) => setLongitud(e.target.value)}
          disabled={isPending}
        />
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

function EstadoBadge({ estado }: { estado: EstadoArranque }): JSX.Element {
  const map: Record<EstadoArranque, BadgeTone> = {
    Activo: "emerald",
    Retirado: "slate",
    Inactivo: "amber",
  };
  return <Badge tone={map[estado]}>{estado}</Badge>;
}

// ────────────────────────────────────────────────────────────────────────
// Panel de foto de referencia del predio
// ────────────────────────────────────────────────────────────────────────

function FotoReferenciaPanel({
  arranque,
}: {
  arranque: ArranqueRead;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [verAmpliada, setVerAmpliada] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const eliminar = useMutation({
    mutationFn: () => eliminarFotoReferencia(arranque.id),
    onSuccess: () => {
      toast.success("Foto eliminada");
      void qc.invalidateQueries({ queryKey: ["arranques"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo eliminar");
    },
  });

  async function handleSubir(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // permitir re-subir el mismo archivo si fuera necesario
    setSubiendo(true);
    try {
      const upload = await apiUploadFile("arranque_referencia", file, {
        filename: file.name,
        contentType: file.type || "image/jpeg",
      });
      await asociarFotoReferencia(arranque.id, upload.key);
      toast.success("Foto actualizada");
      void qc.invalidateQueries({ queryKey: ["arranques"] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo subir");
    } finally {
      setSubiendo(false);
    }
  }

  const tieneFoto = !!arranque.foto_referencia_url;

  return (
    <Card padding="sm">
      <div className="flex items-center gap-2 mb-2">
        <Camera className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-600">
          Foto de referencia del predio
        </span>
        {arranque.foto_referencia_actualizado_at && (
          <span className="text-xs text-neutral-500">
            Actualizada {formatDateTime(arranque.foto_referencia_actualizado_at)}
          </span>
        )}
      </div>
      <div className="flex items-start gap-3">
        {tieneFoto ? (
          <button
            type="button"
            onClick={() => setVerAmpliada(true)}
            className="relative shrink-0 rounded-md overflow-hidden ring-1 ring-neutral-200 hover:ring-brand-500"
            title="Ver ampliada"
          >
            <img
              src={arranque.foto_referencia_url!}
              alt="Predio"
              className="h-24 w-32 object-cover"
              loading="lazy"
            />
          </button>
        ) : (
          <div className="shrink-0 h-24 w-32 rounded-md border-2 border-dashed border-neutral-300 bg-neutral-50 grid place-items-center text-neutral-500">
            <ImageOff className="h-6 w-6" strokeWidth={1.5} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {tieneFoto ? (
            <p className="text-xs text-neutral-600">
              El operador en terreno ve esta imagen al elegir el arranque en
              su ruta. Si el predio cambió, puedes reemplazarla.
            </p>
          ) : (
            <p className="text-xs text-neutral-500">
              Sin foto. La PWA del operador ofrece capturar una al tomar la
              próxima lectura — o puedes subir una manualmente acá.
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            {/* Label con input file — se mantiene nativo (no es <button>) */}
            <label
              className={`inline-flex items-center gap-1 rounded-md border border-neutral-300 px-2.5 py-1 text-xs cursor-pointer hover:bg-neutral-50 ${subiendo ? "opacity-50 pointer-events-none" : ""}`}
            >
              <Upload className="h-3 w-3" strokeWidth={1.5} />
              {subiendo ? "Subiendo…" : tieneFoto ? "Reemplazar" : "Subir foto"}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => void handleSubir(e)}
                className="hidden"
                disabled={subiendo}
              />
            </label>
            {tieneFoto && (
              <Button
                variant="linkDanger"
                size="sm"
                icon={Trash2}
                onClick={() => setConfirmOpen(true)}
                disabled={eliminar.isPending}
              >
                Eliminar
              </Button>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        tone="danger"
        title="Eliminar foto de referencia"
        description={`Se elimina la foto de referencia del arranque ${arranque.codigo_arranque ?? `#${arranque.id}`} (${arranque.direccion}). El operador deja de verla en terreno.`}
        confirmLabel="Eliminar foto"
        loading={eliminar.isPending}
        onConfirm={() => {
          setConfirmOpen(false);
          eliminar.mutate();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      {verAmpliada && tieneFoto && (
        <div
          role="dialog"
          aria-label="Foto de referencia ampliada"
          onClick={() => setVerAmpliada(false)}
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img
            src={arranque.foto_referencia_url!}
            alt="Predio ampliado"
            className="max-h-[85vh] max-w-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Card>
  );
}

